import type { ObservableValue } from '@amazon/vinyl-observable'
import { createLogPrefix, type LogTarget, type Unsubscribe } from '@amazon/vinyl-util'
import { type Auth, AuthStatus } from '@amzn/bf-playback'
import { DesktopAuth } from '@/auth/DesktopAuth'
import { isInstalledDesktop } from '@/device/deviceConfig'
import { handleError } from '@/errorHandler'

const target: LogTarget = { logPrefix: createLogPrefix('desktopSignInGate') }

/** Whether the app is only its sign-in screen, as Morpho's: the desktop app, signed out. */
export function showsDesktopSignIn(auth: Auth): auth is DesktopAuth {
    return (
        isInstalledDesktop() &&
        auth instanceof DesktopAuth &&
        auth.status.value === AuthStatus.SIGNED_OUT
    )
}

/** Shows the desktop sign-in screen in place of the app, until the returned function runs. */
export function mountDesktopSignInScreen(auth: DesktopAuth): Unsubscribe {
    let screen: HTMLElement | null = null
    let disposed = false
    import('@/auth/DesktopSignInScreen')
        .then(({ DesktopSignInScreen }) => {
            if (disposed) return
            screen = DesktopSignInScreen({ auth })
            document.body.append(screen)
        })
        .catch((error: unknown) => handleError(target, error))
    return () => {
        disposed = true
        screen?.remove()
    }
}

/** Remounts the app (`reload`) each time the desktop session starts or ends. */
export function installDesktopSignInGate(
    status$: ObservableValue<AuthStatus>,
    reload: () => void
): Unsubscribe {
    let settled = status$.value
    return status$.onData((status) => {
        if (status === AuthStatus.PENDING || status === settled) return
        settled = status
        reload()
    })
}
